import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import moment from 'moment';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;


const voucherTypes = [
    { value: 'Debit', label: 'Debit' },
    { value: 'Credit', label: 'Credit' }
];

interface VoucherModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        voucher_id?: number;
        voucher_type?: string;
        voucher_amount: number;
        voucher_status: number;
        voucher_date: string;
        voucher_reference: string;
        voucher_note: string;
        voucher_account_from: string;
        voucher_account_to: string;
    } | null;
    isEditMode?: boolean;
}

const VoucherModel: React.FC<VoucherModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddVoucher = async (values: any) => {
        try {
            const dataVal = {
                voucher_type: values.voucher_type,
                voucher_amount: values.voucher_amount,
                voucher_date: values.voucher_date ? values.voucher_date.format('DD-MM-YYYY') : '',
                voucher_reference: values.voucher_reference,
                voucher_note: values.voucher_note,
                voucher_account_from: values.voucher_account_from,
                voucher_account_to: values.voucher_account_to,
                ...(initialValues?.voucher_id && { voucher_id: initialValues.voucher_id }),
            };
            const url = isEditMode
                ? 'edit-voucher'
                : 'add-voucher';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Voucher updated successfully!' : 'Voucher created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} voucher`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                voucher_type: initialValues.voucher_type,
                voucher_amount: initialValues.voucher_amount,
                voucher_date: initialValues.voucher_date ? dayjs(initialValues.voucher_date) : undefined,
                voucher_reference: initialValues.voucher_reference,
                voucher_note: initialValues.voucher_note,
                voucher_account_from: initialValues.voucher_account_from,
                voucher_account_to: initialValues.voucher_account_to,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);



    const validateDecimal = (_: any, value: string) => {
        if (!value) return Promise.resolve();

        // Match decimal with max 15 digits total and max 2 decimal places
        const decimalPattern = /^\d{1,13}(\.\d{1,2})?$/;

        if (!decimalPattern.test(value)) {
            return Promise.reject(
                new Error('Enter a valid amount (up to 13 digits before and 2 after decimal)')
            );
        }

        return Promise.resolve();
    };

    return (
        <Modal
            title={isEditMode ? 'Edit Voucher' : 'Add New Voucher'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddVoucher}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="voucher_type"
                            className="px-8"
                            label={
                                <span>
                                    Voucher Type&nbsp;
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please select a Voucher Type' },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select voucher type"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={voucherTypes}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="voucher_amount"
                            className="px-8"
                            label={
                                <span>
                                    Voucher Amount&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 15 digits total, 2 decimal places)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Voucher Amount' },
                                { validator: validateDecimal },
                            ]}
                        >
                            <Input
                                placeholder="e.g., 123456.78"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="voucher_date"
                            label="Date"
                            className="px-8"
                            rules={[{ required: true, message: 'Please select a Date' }]}
                        >
                            <DatePicker className="py-11" style={{ width: '100%', borderRadius: '20px' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="voucher_reference"
                            className='px-8'
                            label={
                                <span>
                                    Reference&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 100 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { max: 100, message: 'Maximum 100 characters allowed' },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={100}
                                placeholder="e.g., Voucher Reference "

                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="voucher_note"
                            className="px-8"
                            label={
                                <span>
                                    Note&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Optional, Max 100 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                {
                                    max: 100,
                                    message: 'Maximum 100 characters allowed',
                                },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={100}
                                placeholder="e.g., Voucher Reference"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="voucher_account_from"
                            className="px-8"
                            label={
                                <span>
                                    Account From&nbsp;
                                </span>
                            }
                            rules={[
                                {
                                    max: 36,
                                    message: 'Maximum 36 characters allowed',
                                },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={36}
                                placeholder="e.g., account from"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="voucher_account_to"
                            className="px-8"
                            label={
                                <span>
                                    Account To&nbsp;
                                </span>
                            }
                            rules={[
                                {
                                    max: 36,
                                    message: 'Maximum 36 characters allowed',
                                },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={36}
                                placeholder="e.g., account to"
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Voucher
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal >
    );
};

export default VoucherModel;
